import { updateAgreementsPosition } from './shared'

import BodyFreezer from '@/components/BodyFreezerMixin'
import RodoRegulations from '@/components/forms/RodoRegulations.vue'
import { appBeforeInitialize } from '@/events'
import { API } from '@/modules/api'
import { getAgreementsRegulation } from '@/services/RodoRegulations'
import QueryString from '@/utils/QueryString'

/**
 * Base class for field validators
 */
class Validator {
  constructor (field) {
    this.field = field
  }

  markFieldAsValid () {
    this.field.parentElement.classList.add('fieldValid')
  }

  markFieldAsInvalid () {
    this.field.parentElement.classList.remove('fieldValid')
  }

  isFieldValid () {
    throw new Error('Not implemented')
  }

  validate () {
    if (this.isFieldValid()) {
      this.markFieldAsValid()
      return true
    } else {
      this.markFieldAsInvalid()
      return false
    }
  }
}

/**
 * Validator for name field
 */
class NameFieldValidator extends Validator {
  constructor (input) {
    super(input)
    this.input = input
  }

  isFieldValid () {
    const minLenght = this.input.getAttribute('data-val-length-min')
    const pattern = this.input.getAttribute('data-val-multiregex-pattern')
    const rx = new RegExp(pattern)
    return (rx.exec(this.input.value) != null) && this.input.value.length >= minLenght
  }
}

/**
 * Validator for email field
 */
class EmailFieldValidator extends Validator {
  constructor (input) {
    super(input)
    this.input = input
  }

  isFieldValid () {
    const pattern = this.input.getAttribute('data-val-regex-pattern')
    const rx = new RegExp(pattern)
    return rx.exec(this.input.value) != null
  }
}

export default class Popup {
  /**
   * 
   * @param {Element} root popup element 
   * @param {String} userEmail 
   */
  constructor (root, userEmail) {
    this.root = root
    this.userEmail = userEmail
    this.mutatedWffmForm = null
    this.agreementsControl = false
    this.rodoRegulations = []

    this.newsletterSignSection = this.root.querySelector('#newsletter-signin')
    this.newsletterConfirmSection = this.root.querySelector('#newsletter-confirm')
    this.newsletterWFFMFormWrapper = this.newsletterSignSection.querySelector('.newsletter-signin--form')
    this.newsletterWFFMForm = this.newsletterWFFMFormWrapper.querySelector('form')
    this.parameterName = this.newsletterWFFMFormWrapper.dataset.utmPhrase
  }

  init () {
    if (!this.getWffmForm()) return this.wffmFormNotExistWarning()

    this.styleNewsletterAgreements()
    this.styleValidationOnUserInput()
    this.agreementCheckboxesState()
    this.submitWffmForm()
    this.setEmailPlaceholder()
    this.moveFbButtonToForm()
    this.unfreezeBodyOnClose()

    if (this.isSitecoreEditor()) {
      this.toggleEEView()
    }

    return appBeforeInitialize.subscribe(() => {
      new API().getUtmParameterValuesList()
        .then(data => this.initComponentOnPageStart(data.list))
    })
  }

  wffmFormNotExistWarning () {
    log.warn('Warning! WFFM form not exist, code below will be skipped.')
  }

  parameterValueMatchSitecoreList (list) {
    const parameterValue = new QueryString().get(this.parameterName)

    for (const val of list) {
      if (parameterValue === val) return true
    }

    return false
  }

  isSitecoreEditor () {
    return this.root.classList.contains('newsletterSignin_experienceEdit')
  }

  initComponentOnPageStart (list) {
    if (this.isSitecoreEditor() || this.parameterValueMatchSitecoreList(list)) {
      this.showComponent()
    }
  }

  getNameFieldFromWFFM () {
    const form = this.getWffmForm()
    return form.querySelector('.form-control[type="text"]')
  }

  getEmailFieldFromWffm () {
    const form = this.getWffmForm()
    return form.querySelector('.form-control[type="email"]')
  }

  getEmailValueFromWFFMField () {
    return this.getEmailFieldFromWffm().value
  }

  getWffmForm () {
    return this.mutatedWffmForm ? this.mutatedWffmForm.target : this.newsletterWFFMForm    
  }

  /**
   * 
   * @param {Element} form 
   */
  getFormSubmitButton () {
    const form = this.getWffmForm()
    return form.querySelector('input[type="submit"]')
  }

  /**
   * 
   * @param {Element} parent html block
   */
  getAgreementsHtmlBlock () {
    const form = this.getWffmForm()
    return form.querySelector('.agreements')
  }

  getNumberOfCheckboxes () {
    const agreements = this.getAgreementsHtmlBlock()
    return agreements.querySelectorAll('input[type="checkbox"]').length
  }

  getNumberOfCheckedAgreements () {
    const agreements = this.getAgreementsHtmlBlock()
    const inputElements = agreements.querySelectorAll('input[type="checkbox"]')
    return inputElements.filter(el => el.checked).length
  }
  
  /**
   * 
   * @param {String} email 
   */
  setUserEmailValue (email) {
    const emailField = this.getEmailFieldFromWffm()
    if (emailField) emailField.value = email
  }

  showComponent () {
    if (!this.isSitecoreEditor()) BodyFreezer.methods.freezeBody()

    if (this.root.classList.contains('fadeOut')) {
      this.root.classList.remove('fadeOut')
    }
    
    this.root.classList.add('fadeIn')
    this.setUserEmailValue(this.userEmail)
    this.getRodoRegulations()
  }

  unfreezeBodyOnClose () {
    const closeButton = this.root.querySelector('.modal__close')
    closeButton.addEventListener('click', () => BodyFreezer.methods.unfreezBody())
  }

  createRodoRegulationsPlaceholder () {
    const div = document.createElement('div')
    div.classList.add('rodo-regulations-component')
    return div
  }

  rodoRegulationsPlaceholderExist () {
    const agreements = this.getAgreementsHtmlBlock()
    return agreements.querySelector('.rodo-regulation')
  }

  getRodoRegulations () {
    return getAgreementsRegulation()
      .then(data => { this.rodoRegulations = data })
      .then(() => this.addRodoRegulationsComponent())
  }

  addRodoRegulationsComponent () {
    const placeholder = this.createRodoRegulationsPlaceholder()
    const agreements = this.getAgreementsHtmlBlock()
    agreements.insertAdjacentElement('beforeend', placeholder)
    
    if (!this.rodoRegulationsPlaceholderExist()) {
      new RodoRegulations({
        el: placeholder,
        propsData: {
          preamble: this.rodoRegulations.preamble,
          body: this.rodoRegulations.body,
        },
      })
    }
  }

  /**
   * We need to change agreements place because this html block is rendered by wffm 
   */
  styleNewsletterAgreements () {
    const form = this.getWffmForm()
    const agreements = form.querySelector('.form-submit-border').previousElementSibling
    const agreementsInfo = this.newsletterSignSection.querySelector('.agreements-info').cloneNode(true)
    const personalDataProcessing = this.newsletterSignSection.querySelector('.agreements-personalData').cloneNode(true)

    const hasAgreementsInfo = () => agreements.querySelector('.agreements-info') != null
    const hasPersonalData = () => agreements.querySelector('.agreements-personalData') != null

    const setLabelForCheckbox = checkbox => {
      const inputId = checkbox.querySelector('input').getAttribute('id')
      checkbox.querySelector('label').setAttribute('for', inputId)
    }
    const setLabelForCheckboxes = checkboxes => {
      checkboxes.forEach(setLabelForCheckbox)
    }

    agreements.classList.add('agreements')

    if (!hasAgreementsInfo()) {
      agreements.insertAdjacentElement('afterbegin', agreementsInfo)
    }
    if (!hasPersonalData()) {
      agreements.insertAdjacentElement('beforeend', personalDataProcessing)
    }

    const checkboxes = agreements.querySelectorAll('.checkbox')
    setLabelForCheckboxes(checkboxes)
  }

  showAgreements () {
    const agreements = this.getAgreementsHtmlBlock()
    this.agreementsControl = true

    agreements.classList.add('show')
    updateAgreementsPosition(agreements, this.newsletterSignSection)
  }

  showAgreementsIfFormFieldsAreValid () {
    if (this.formFieldsAreValid()) this.showAgreements()
  }

  showElement (element) {
    if (element) element.style.display = 'block'
  }

  hideElement (element) {
    if (element) element.style.display = 'none'
  }

  nameFieldIsValid () {
    const nameInput = this.getNameFieldFromWFFM()
    return new NameFieldValidator(nameInput).validate()
  }

  emailFieldIsValid () {
    const emailInput = this.getEmailFieldFromWffm()
    return new EmailFieldValidator(emailInput).validate()
  }

  formFieldsAreValid () {
    return this.nameFieldIsValid() && this.emailFieldIsValid()
  }

  styleValidationOnUserInput () {
    const nameInput = this.getNameFieldFromWFFM()
    const emailInput = this.getEmailFieldFromWffm()
    nameInput.addEventListener('input', () => this.showAgreementsIfFormFieldsAreValid())
    emailInput.addEventListener('input', () => this.showAgreementsIfFormFieldsAreValid())
  }

  agreementCheckboxesState () {
    const agreements = this.getAgreementsHtmlBlock()
    const inputElements = agreements.querySelectorAll('input[type="checkbox"]')

    const checkboxState = () => {
      const checkedAgreementsCount = this.getNumberOfCheckedAgreements()

      if (checkedAgreementsCount === this.getNumberOfCheckboxes()) {
        this.hideAgreementRequirementsWarning()
        updateAgreementsPosition(agreements, this.newsletterSignSection)
      } else {
        this.addAgreementRequirementsWarning()
      }
    }

    inputElements.forEach(input => {
      input.addEventListener('change', checkboxState)
    })
  }

  createFormLoader () {
    const loader = document.createElement('div')
    const loaderFigure = document.createElement('div')
    loader.classList.add('loader')
    loaderFigure.classList.add('loaderFigure')
    loader.appendChild(loaderFigure)
    return loader
  }

  showConfirmationSection () {    
    const description = this.newsletterSignSection.querySelector('.newsletter-signin--description')
    description.insertAdjacentElement('beforeend', this.createFormLoader())
    this.newsletterWFFMForm.classList.add('hidden')

    const confirmation = setTimeout(() => {
      // Toggle sections
      this.newsletterSignSection.classList.add('hidden')
      this.newsletterConfirmSection.classList.add('show')

      clearTimeout(confirmation)
    }, 1000)
  }

  hideAgreementRequirementsWarning () {
    this.newsletterSignSection.querySelector('.agreements-warning').classList.remove('show')
    this.root.querySelector('.newsletter-signin--description').classList.remove('form-warning')
  }

  addAgreementRequirementsWarning () {
    const agreements = this.getAgreementsHtmlBlock()
    this.root.querySelector('.agreements-warning').classList.add('show')
    agreements.classList.add('has-error')
    this.root.querySelector('.newsletter-signin--description').classList.add('form-warning')
    updateAgreementsPosition(agreements, this.newsletterSignSection)
  }

  isMutationWarningBlock (warningElement) {
    return warningElement && warningElement.nodeType == 1 && warningElement.classList.contains('bg-warning')
  }

  wffmFormHasError () {
    // Class ".bg-warning" comes from WFFM so there we know that there is some validation warning
    return this.newsletterSignSection.querySelector('div.bg-warning')
  }

  observeWffmFormMutations () {
    const target = this.newsletterWFFMForm
    const config = { attributes: false, childList: true, characterData: true }
    
    return new Promise((resolve, reject) => {
      const observer = new MutationObserver(mutations => {
        if (mutations.length) {
          this.mutatedWffmForm = mutations[0]
          resolve(mutations[0])
        }

        reject('No mutations: ', mutations.length)
      })
      
      observer.observe(target, config)
    })
  }

  submitWffmForm () {
    const confirmSectionEmailPlaceholder = this.newsletterConfirmSection.querySelector('span.userEmail')
    let submitButton = this.getFormSubmitButton()

    const sendForm = () => {
      const checkedAgreementsCount = this.getNumberOfCheckedAgreements()
      confirmSectionEmailPlaceholder.innerText = this.getEmailValueFromWFFMField()
      
      if (checkedAgreementsCount !== this.getNumberOfCheckboxes() && this.agreementsControl) {
        this.addAgreementRequirementsWarning()
      }
      
      return this.observeWffmFormMutations()
        .then(mutationRecord => {
          const targetForm = mutationRecord.target
          const warningElement = mutationRecord.addedNodes[4]
          submitButton = this.getFormSubmitButton()
          
          if (this.isMutationWarningBlock(warningElement) || this.wffmFormHasError()) {
            targetForm.classList.add('emailValidationError')
            this.styleValidationOnUserInput()
            this.styleNewsletterAgreements()
            this.agreementCheckboxesState()
            this.moveFbButtonToForm()
            this.toggleEmailValidationBlock()
            this.addRodoRegulationsComponent()
            updateAgreementsPosition(this.getAgreementsHtmlBlock(), this.newsletterSignSection)
            
            // Need to attach 2nd time the same event because whole form is replaced by WFFM
            submitButton.addEventListener('click', sendForm)  
          } else {
            this.newsletterSignSection.querySelector('.agreements-warning').classList.add('hidden')
            this.newsletterSignSection.querySelector('.newsletter-signin--description').classList.remove('form-warning')
            this.showConfirmationSection()
          }
        })
        .catch(log.error)
    }

    submitButton.addEventListener('click', sendForm)
  }

  setEmailPlaceholder () {
    const newsletterWrapper = this.newsletterWFFMFormWrapper
    const emailPlaceholderText = newsletterWrapper.querySelector('.emailPlaceholderText').innerText
    const emailInput = this.getEmailFieldFromWffm()

    return emailInput.setAttribute('placeholder', emailPlaceholderText)
  }

  toggleEmailValidationBlock () {
    const emailInputParent = this.getEmailFieldFromWffm().parentElement
    const validationBlock = this.getWffmForm().querySelector('div.bg-warning')
    const validationText = validationBlock.innerText
    
    if (validationBlock) {
      emailInputParent.classList.add('has-error')
      emailInputParent.querySelector('.help-block').innerText = validationText
    }
  }

  toggleEEView () {
    const agreements = this.getAgreementsHtmlBlock()
    const showSigninSectionButton = this.root.querySelector('button.view-signin')
    const showConfirmSectionButton = this.root.querySelector('button.view-confirmation')
    const modalInner = this.root.querySelector('.modal__inner')

    /**
     * Better is to change elements style.display than handling with classNames
     * 
     * @param {Event} e 
     */
    const viewToggle = e => {
      if (e.target.classList.contains('view-signin')) {
        this.newsletterSignSection.style.display = 'flex'
        this.newsletterConfirmSection.style.display = 'none'
      } else {
        this.newsletterSignSection.style.display = 'none'
        this.newsletterConfirmSection.style.display = 'block'
      }
    }

    agreements.classList.add('eeView')
    modalInner.insertAdjacentElement('beforeend', agreements)

    showSigninSectionButton.addEventListener('click', viewToggle)
    showConfirmSectionButton.addEventListener('click', viewToggle)
  }
  onFbSuccessCenterDefaultButton () {
    return this.newsletterWFFMFormWrapper.classList.add('center-btn')
  }
  moveFbButtonToForm () {
    const fbGroup = this.newsletterSignSection.querySelector('.facebook-group').cloneNode(true)
    const form = this.getWffmForm()

    if (!form.querySelector('.facebook-group')) {
      form.insertAdjacentElement('beforeend', fbGroup)
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/newsletter/Popup.js